import { useState, useRef, useContext } from "react";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { SiteContext } from "@/App";
import { Loader2, CheckCircle, ImagePlus, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BODY_AREAS = [
  "Full Back", "Full Sleeve", "Half Sleeve", "Chest", "Ribcage",
  "Leg", "Thigh", "Forearm", "Upper Arm", "Neck", "Hand / Fingers",
  "Foot / Ankle", "Other"
];

export default function ContactForm() {
  const { API } = useContext(SiteContext);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    body_area: "", description: "",
  });
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "First name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = "Invalid email address";
    if (!form.body_area) errs.body_area = "Please select a body area";
    if (!form.description.trim()) errs.description = "Please describe your concept";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.error("Please fix the errors below.");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("first_name", form.first_name);
      formData.append("last_name", form.last_name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);
      formData.append("body_area", form.body_area);
      formData.append("description", form.description);
      images.forEach(img => formData.append("images", img.file));
      await axios.post(`${API}/contact`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Your consultation request has been sent!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 4 - images.length;
    if (remaining <= 0) {
      toast.error("Maximum 4 images allowed.");
      return;
    }
    const valid = files.filter(f => f.type.startsWith("image/")).slice(0, remaining);
    if (valid.length < files.length) toast.error("Only image files are accepted.");
    const newImages = valid.map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setImages(prev => [...prev, ...newImages]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (index) => {
    setImages(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <section data-testid="contact-section" ref={ref} className="py-24 md:py-32 bg-ink-900 relative overflow-hidden">
      {/* DSC01490-2 — B&W smiling studio portrait as ambient background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.08]"
        style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_lud-hub/artifacts/h6hoah0q_DSC01490-2.jpeg)` }}
      />
      <div className="absolute inset-0 bg-gradient-to-l from-ink-900 via-ink-900/70 to-ink-900" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left — Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <p className="font-manrope text-verde text-xs tracking-[0.3em] uppercase mb-4">Get in Touch</p>
            <div className="section-divider mb-8" />
            <h2 className="font-cinzel text-4xl md:text-5xl font-bold text-[#E5E5E5] leading-tight mb-8">
              Book Your<br />Consultation
            </h2>

            <div className="space-y-6 text-[#A3A3A3] font-manrope text-sm leading-relaxed">
              <p>
                All new projects begin with a free consultation. Fill out the form to share your concept — Lud personally reviews every application and selects projects that align with his artistic vision.
              </p>
              <p>
                Please include as much detail as possible about your idea, placement, and any reference images in your description. High-detail black and grey realism pieces typically require multiple sessions.
              </p>
            </div>

            <div className="mt-10 space-y-4 border-t border-ink-600 pt-8">
              <div>
                <p className="font-manrope text-[10px] tracking-widest uppercase text-[#525252]">Studio</p>
                <p className="font-manrope text-sm text-[#A3A3A3] mt-1">314 6th Street, New Westminster, BC</p>
              </div>
              <div>
                <p className="font-manrope text-[10px] tracking-widest uppercase text-[#525252]">Email</p>
                <a href="mailto:ludbradley.tattoo@gmail.com" className="font-manrope text-sm text-verde hover:text-verde/70 transition-colors mt-1 block">
                  ludbradley.tattoo@gmail.com
                </a>
              </div>
              <div>
                <p className="font-manrope text-[10px] tracking-widest uppercase text-[#525252]">Books Status</p>
                <p className="font-manrope text-sm text-verde mt-1 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-verde inline-block animate-pulse" />
                  Open for New Projects
                </p>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div data-testid="studio-map" className="mt-8 rounded-sm overflow-hidden border border-ink-600">
              <iframe
                title="Avalon Tattoo Collective — Studio Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2607.0!2d-122.9117!3d49.2013!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5485d8a1c3b1b8f5%3A0x0!2s314+6th+St%2C+New+Westminster%2C+BC!5e0!3m2!1sen!2sca!4v1700000000000"
                width="100%"
                height="200"
                style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) contrast(0.9)" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {submitted ? (
              <div data-testid="contact-success" className="h-full flex flex-col items-center justify-center text-center py-16 gap-6">
                <CheckCircle size={48} className="text-verde" />
                <h3 className="font-cinzel text-2xl font-bold text-[#E5E5E5]">Request Received</h3>
                <p className="font-manrope text-[#A3A3A3] text-sm max-w-sm">
                  Lud personally reviews every application. You&apos;ll hear back within 3–5 business days.
                </p>
                <button className="btn-ghost mt-4" onClick={() => setSubmitted(false)}>Send Another</button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">
                      First Name *
                    </label>
                    <input
                      data-testid="contact-first-name"
                      name="first_name"
                      value={form.first_name}
                      onChange={handleChange}
                      className={`w-full bg-ink-800 border focus:border-verde text-[#E5E5E5] font-manrope text-sm px-4 py-3 rounded-sm outline-none transition-colors placeholder-[#525252] ${errors.first_name ? "border-red-500/60" : "border-ink-600"}`}
                      placeholder="First"
                    />
                    {errors.first_name && <p data-testid="error-first-name" className="font-manrope text-[10px] text-red-400 mt-1">{errors.first_name}</p>}
                  </div>
                  <div>
                    <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">
                      Last Name
                    </label>
                    <input
                      data-testid="contact-last-name"
                      name="last_name"
                      value={form.last_name}
                      onChange={handleChange}
                      className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-sm px-4 py-3 rounded-sm outline-none transition-colors placeholder-[#525252]"
                      placeholder="Last"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">
                    Email *
                  </label>
                  <input
                    data-testid="contact-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className={`w-full bg-ink-800 border focus:border-verde text-[#E5E5E5] font-manrope text-sm px-4 py-3 rounded-sm outline-none transition-colors placeholder-[#525252] ${errors.email ? "border-red-500/60" : "border-ink-600"}`}
                    placeholder="your@email.com"
                  />
                  {errors.email && <p data-testid="error-email" className="font-manrope text-[10px] text-red-400 mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">
                    Phone
                  </label>
                  <input
                    data-testid="contact-phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-sm px-4 py-3 rounded-sm outline-none transition-colors placeholder-[#525252]"
                    placeholder="+1 (604) 000-0000"
                  />
                </div>

                <div>
                  <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">
                    Body Area *
                  </label>
                  <Select
                    value={form.body_area}
                    onValueChange={(val) => {
                      setForm(prev => ({ ...prev, body_area: val }));
                      if (errors.body_area) setErrors(prev => ({ ...prev, body_area: "" }));
                    }}
                  >
                    <SelectTrigger
                      data-testid="contact-body-area"
                      className={`w-full bg-ink-800 border h-auto font-manrope text-sm px-4 py-3 rounded-sm text-[#E5E5E5] ${errors.body_area ? "border-red-500/60" : "border-ink-600"} focus:ring-verde focus:border-verde`}
                    >
                      <SelectValue placeholder="Select area..." />
                    </SelectTrigger>
                    <SelectContent className="bg-ink-800 border-ink-600 z-[9999]">
                      {BODY_AREAS.map(a => (
                        <SelectItem
                          key={a}
                          value={a}
                          className="font-manrope text-sm text-[#E5E5E5] focus:bg-verde/20 focus:text-[#E5E5E5] cursor-pointer"
                        >
                          {a}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.body_area && <p data-testid="error-body-area" className="font-manrope text-[10px] text-red-400 mt-1">{errors.body_area}</p>}
                </div>

                <div>
                  <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">
                    Describe Your Concept *
                  </label>
                  <textarea
                    data-testid="contact-description"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows={5}
                    className={`w-full bg-ink-800 border focus:border-verde text-[#E5E5E5] font-manrope text-sm px-4 py-3 rounded-sm outline-none transition-colors resize-none placeholder-[#525252] ${errors.description ? "border-red-500/60" : "border-ink-600"}`}
                    placeholder="Tell Lud about your vision — subject matter, style references, size, any existing tattoos to consider..."
                  />
                  {errors.description && <p data-testid="error-description" className="font-manrope text-[10px] text-red-400 mt-1">{errors.description}</p>}
                </div>

                {/* Inspiration Images Upload */}
                <div>
                  <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">
                    Inspiration Images (up to 4)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                  />

                  {images.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {images.map((img, i) => (
                        <div key={i} className="relative aspect-square rounded-sm overflow-hidden border border-ink-600 group">
                          <img src={img.preview} alt={`Inspiration ${i + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            data-testid={`remove-image-${i}`}
                            onClick={() => removeImage(i)}
                            className="absolute top-1 right-1 bg-black/70 hover:bg-red-500/80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={10} className="text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {images.length < 4 && (
                    <button
                      type="button"
                      data-testid="add-inspiration-images"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border border-dashed border-ink-600 hover:border-verde/50 rounded-sm py-4 flex items-center justify-center gap-3 transition-colors group"
                    >
                      <ImagePlus size={16} className="text-[#525252] group-hover:text-verde transition-colors" />
                      <span className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] group-hover:text-[#A3A3A3] transition-colors">
                        {images.length === 0 ? "Add reference images" : `Add more (${4 - images.length} remaining)`}
                      </span>
                    </button>
                  )}
                </div>

                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full btn-verde flex items-center justify-center gap-3 py-4 disabled:opacity-60"
                >
                  {loading ? (
                    <><Loader2 size={16} className="animate-spin" /> Sending...</>
                  ) : (
                    "Send Application"
                  )}
                </button>

                <p className="font-manrope text-[10px] text-[#525252] text-center">
                  Free consultation. No commitment. Lud personally reviews all applications.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
